import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects'
import {
  delay,
  distinctUntilChanged,
  filter,
  first,
  map,
  mapTo,
  mergeMap,
  observeOn,
  scan,
  switchMap,
  takeUntil,
  tap
} from 'rxjs/operators'
import { Router } from '@angular/router'
import {
  alertError,
  copyToClipboard,
  deleteStorageValue,
  empty,
  initStorageState,
  navigate,
  notificationMessage,
  notificationWarning,
  openConfirmModal,
  patchStorageValue,
  saveFile,
  sendGTMEvent,
  setLoadingState,
  setStorageStateValue,
  setStorageValue,
  unauthorizedUser
} from '../actions/global.actions'
import { LoadingStateType } from '@adex/shared/models/states/loading.state'
import { environment } from '../../../environments'
import { NzNotificationService } from 'ng-zorro-antd/notification'
import { TranslateService } from '@ngx-translate/core'
import { LocalStorageService } from 'ngx-webstorage'
import { StorageStateCreator } from '@adex/store/utils'
import { asapScheduler, merge, Subject, timer } from 'rxjs'
import { FileSavingService } from '@adex/shared/services/file-saving.service'
import { Action, Store } from '@ngrx/store'
import { selectFromStorage, selectUrl } from '@adex/store/selectors'
import { catchErrorLog } from '@adex/shared/utils/errors'
import { NzModalService } from 'ng-zorro-antd/modal'
import { ClipboardService } from 'ngx-clipboard'
import { GtmService } from '@adex/shared/services/gtm.service'
import { IUser } from '@adex/shared/models/user'
import { getGTMEvent, GTMEventAlias, GtmEvents } from '@adex/shared/models/gtm-events'
import { on, when } from '@adex/shared/utils/when'
import { appRoutes } from '@adex/shared/routing/routes'


@Injectable()
export class GlobalEffects {

  public readonly initStorageState$ = createEffect(() => this.actions$.pipe(
    ofType(ROOT_EFFECTS_INIT),
    map(() => StorageStateCreator.init(this.localStorage, (key, value) => this.store.dispatch(setStorageStateValue({ key, value })))),
    map(state => initStorageState({ state }))
  ))

  public readonly navigate$ = createEffect(() => this.actions$.pipe(
    ofType(navigate),
    map(({ commands, extras }) => ({ commands: typeof commands === 'string' ? [ commands ] : commands, extras })),
    tap(({ commands, extras }) => this.router.navigate(commands, extras))
  ), { dispatch: false })

  public readonly loading$ = createEffect(() => this.actions$.pipe(
    ofType(setLoadingState.type),
    filter(({ state }) => state === LoadingStateType.error || state === LoadingStateType.success),
    delay(environment.loadingStateChangeDelay),
    map(({ url, state }) => setLoadingState({ url, state: LoadingStateType.default }))
  ))

  public readonly alertError$ = createEffect(() => this.actions$.pipe(
    ofType(alertError.type),
    mergeMap(({ message }) => this.translate.get(message)),
    map(message => this.getError(message)),
    tap((message) => this.notification.error(message.title, message.content))
  ), { dispatch: false })

  public readonly notification$ = createEffect(() => this.actions$.pipe(
    ofType(notificationMessage.type),
    mergeMap(({ message }) => this.translate.get(message)),
    map(message => this.getNotification(message)),
    tap((message) => this.notification.info(message.title, message.content))
  ), { dispatch: false })

  public readonly warning$ = createEffect(() => this.actions$.pipe(
    ofType(notificationWarning.type),
    mergeMap(({ message }) => this.translate.get(message)),
    map(message => this.getNotification(message)),
    tap((message) => this.notification.warning(message.title, message.content))
  ), { dispatch: false })

  public readonly setStorageState$ = createEffect(() => this.actions$.pipe(
    ofType(setStorageValue),
    observeOn(asapScheduler),
    tap(({ key, value }) => this.localStorage.store(key, value)),
    map(({ key, value }) => setStorageStateValue({ key, value }))
  ))

  public readonly patchStorageState$ = createEffect(() => this.actions$.pipe(
    ofType(patchStorageValue),
    switchMap(({ key, value, patcher }) => this.store.select(selectFromStorage(key)).pipe(
      first(),
      map(state => setStorageValue({ key, value: patcher(state, value) })),
      catchErrorLog()
    ))
  ))

  public readonly deleteStorageState$ = createEffect(() => this.actions$.pipe(
    ofType(deleteStorageValue.type),
    observeOn(asapScheduler),
    map(({ key }) => this.localStorage.clear(key))
  ), { dispatch: false })

  public readonly unauthorizedUser$ = createEffect(() => this.actions$.pipe(
    ofType(unauthorizedUser.type),
    mapTo(deleteStorageValue({ key: 'user' }))
  ))

  public readonly saveFile$ = createEffect(() => this.actions$.pipe(
    ofType(saveFile),
    tap(({ file }) => this.saver.save(file))
  ), { dispatch: false })

  private readonly openConfirmModal$ = createEffect(() => this.actions$.pipe(
    ofType(openConfirmModal),
    map(({ action, translateObjectName }) => {
      const contentDefault = this.translate.instant('DEFAULT_MODAL_CONTENT')
      let content = {}
      try {
        content = this.translate.instant(translateObjectName)
      } catch {
      }
      return [ action, { ...contentDefault, ...content } ]
    }),
    switchMap(([ actionFactory, contentObj ]) => {
      const subject: Subject<Action> = new Subject()
      const modelRef = this.modal.confirm({
        nzTitle: contentObj.TITLE,
        nzContent: contentObj.CONTENT,
        nzOkText: contentObj.OK_TEXT,
        nzOkDanger: contentObj.OK_TYPE === 'danger',
        nzCancelText: contentObj.CANCEL_TEXT,
        nzOnOk: () => subject.next(actionFactory()),
        nzOnCancel: () => subject.next(empty())
      })
      const timerClose = timer(environment.autoCloseConfirmModalDelay).pipe(
        tap(() => modelRef.close()),
        mapTo(empty()),
        takeUntil(subject)
      )
      return merge(subject, timerClose)
    })
  ))

  private readonly copyToClipboard$ = createEffect(() => this.actions$.pipe(
    ofType(copyToClipboard),
    tap(({ content }) => this.clipboard.copy(content))
  ), { dispatch: false })

  private readonly setGTMUserId$ = createEffect(() => this.store.select(selectFromStorage('user')).pipe(
    filter<IUser>(Boolean),
    map(user => user.id),
    distinctUntilChanged(),
    tap(id => this.gtm.setUserId(id))
  ), { dispatch: false })

  private readonly sendGTMEvent$ = createEffect(() => this.actions$.pipe(
    ofType(sendGTMEvent),
    tap(({ event }) => this.gtm.send(event))
  ), { dispatch: false })

  private readonly sendGTMUrlPath$ = createEffect(() => this.store.select(selectUrl).pipe(
    filter<string>(Boolean),
    map(url => url.includes('?') ? url.slice(0, url.indexOf('?')) : url),
    map(url => sendGTMEvent({ event: getGTMEvent(GTMEventAlias.route, { virtualPagePath: url }) }))
  ))

  private readonly currentRoute$ = createEffect(() => this.store.select(selectUrl).pipe(
    scan((lastRoute, currentRoute) => {
      const event = this.getGTMEventByRouting(lastRoute, currentRoute)
      if (event) this.store.dispatch(sendGTMEvent({ event }))
      return currentRoute
    }),
  ), { dispatch: false })

  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly store: Store,
    private readonly translate: TranslateService,
    private readonly localStorage: LocalStorageService,
    private readonly notification: NzNotificationService,
    private readonly saver: FileSavingService,
    private readonly modal: NzModalService,
    private readonly clipboard: ClipboardService,
    private readonly gtm: GtmService
  ) {
  }

  private getMessage(state: string, msg: any): { title: string, content: string } {
    const key = `${ state }DEFAULT_TITLE`
    const defaultTitle = this.translate.instant(key)
    if (defaultTitle === key) throw new Error(`State ${ state } does not have default title`)
    if (typeof msg === 'string') return { title: defaultTitle, content: msg }
    if (!Reflect.has(msg, 'CONTENT') || !Reflect.has(msg, 'TITLE')) throw new Error(`State ${ state } does not have title or content`)
    const title = Reflect.get(msg, 'TITLE')
    const content = Reflect.get(msg, 'CONTENT')
    return { title, content }
  }

  private getError(error: any): { title: string, content: string } {
    return this.getMessage('ERROR_MESSAGES.', error)
  }

  private getNotification(notification: any): { title: string, content: string } {
    return this.getMessage('NOTIFICATIONS.', notification)
  }

  private getGTMEventByRouting(lastRoute: string, currentRoute: string): GtmEvents {
    return when<null, GtmEvents>(null,
      on(() => lastRoute === appRoutes.auth.login && currentRoute.includes(appRoutes.app.main), () => getGTMEvent(GTMEventAlias.login)),
      on(() => lastRoute === appRoutes.auth.setPassword && !lastRoute.includes('token') && currentRoute.includes(appRoutes.app.main),
        () => getGTMEvent(GTMEventAlias.registration))
      )
  }

}
